import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'simplebar';
import 'simplebar/dist/simplebar.css';
import FileSaver from 'file-saver';
import { get, post } from 'axios';
import { useHistory } from 'react-router';
import Papa from 'papaparse';
import { BiSolidError, BiX, BiCheck } from 'react-icons/bi';
import { FaCheckSquare } from 'react-icons/fa';
import ClipLoader from 'react-spinners/ClipLoader';
import EditTable from './Components/EditTable';
import { uploadFileSettings, coverageRequest, uploadBacktest, updateBulkBasket } from './utils/api';
import BitaInput from '../../components/BitaInput/BitaInput';
import BitaButton from '../../bitaComponents/BitaButton/BitaButton';
import BitaSelect from '../../components/BitaSelect/BitaSelect';
import BitaModal from '../../bitaComponents/BitaModal/BitaModal';
import BitaSpinner from '../../components/BitaSpinner/BitaSpinner';
import { ReactComponent as Logo } from '../../static/img/Icons/BITA.svg';
import {
  toastInterceptor,
  hasValidUBSAPACOnly,
  hasValidBitaDomain,
  hasValidRICAccess,
  hasValidMarketVectorOrgAll,
  hasValidClearStreetAmericas,
} from '../../utils/utils';
import {
  CURRENCIES_ARRAY,
  DATA_APPLICATION_POINT,
  DATA_CASH_DISTRIBUTION,
  DATA_DIVISOR_DETERMINATION_SETUP,
  SHARES_DETERMINATION_POINT,
  MAX_LAUNCH_DATE,
  TYPE_OF_MODIFICATION,
  CALCULATION_ZONE_FOR_MARKET_VECTOR,
  EASTERN_APAC_NAME,
  CALCULATION_ZONE_CONFIG,
  IDENTIFIER_TYPE,
  AT_MARKET_OPEN,
} from './utils/constants';
import { URL_CONSTANTS } from '../../api-data-mapping/url-constants';
import './Portfolio.css';
import BitaTable from '../../bitaComponents/BitaTable/BitaTable';
import BitaSearchBar from '../../bitaComponents/BitaSearchBar/BitaSearchBar';
import BitaCalendarDate from '../../components/BitaCalendar/BitaCalendarDate';
import BitaCheckbox from '../../components/BitaCheckbox/BitaCheckbox';
import Switcher from '../../bitaComponents/Switcher/Switcher';
import VisualizationModal from './withholding-tax/VisualizationModal';
import ShowPreview from './utils/ShowPreview';
import {
  isFloatWithMoreThanTwoDecimals,
  existRequestCoverageWithData,
  buildRequestCoverageBody,
  orderByType,
  generateEffectiveDate,
  handleError,
  handleDownloadTemplate,
  updateCoverageHelper,
  isValidExcelFile,
  getZoneValue,
  handleCalculationZones,
} from './utils';

import style from './update-basket.module.scss';
import styles from './PortfolioBasketCalculation.module.scss';
import tableStyles from './table.module.scss';
import { AddInstrumentModal } from './Modals/AddInstrumentModal';
import { ORGANIZATION_PREFIXES } from '../../constants/organizationPrefixConstants';

const getBasket = (tokenUser, data) => {
  return get(URL_CONSTANTS.GETLIVEBASKET, {
    headers: { Authorization: `Bearer ${tokenUser}` },
  });
};

const getBasketById = (tokenUser, basket) => {
  return get(`${URL_CONSTANTS.GETLIVEBASKET}/${basket}`, {
    headers: { Authorization: `Bearer ${tokenUser}` },
  });
};

const getMIC = (tokenUser, data) => {
  return get(URL_CONSTANTS.ACE_MIC_ALL_URL, {
    headers: { Authorization: `Bearer ${tokenUser}` },
  });
};

const recalculateCoverage = (tokenUser, data) => {
  return post(URL_CONSTANTS.COVERAGE_REQUEST_UPDATE_URL, data, {
    headers: { Authorization: `Bearer ${tokenUser}` },
  });
};

const url = `${process.env.REACT_APP_API_URL}/ace/basket/mic/all`;

function truncateString(str, limit) {
  if (str.length > limit) {
    return `${str.substring(0, limit)}...`;
  }
  return str;
}

const getBasketSearchOptions = (listBasket = []) =>
  [...listBasket]
    .sort((a, b) => (a?.symbol ?? '').localeCompare(b?.symbol ?? ''))
    .map(item => ({
      label: item?.symbol,
      key: item?.id,
      value: item?.symbol,
      id: item?.id,
      name: item?.symbol,
      item,
    }));

const basketButton = {
  'Long only Basket': [
    {
      type: 'Button',
      label: 'Submission with Weights',
      url: 'divisor_basket_input_with_open_weight',
    },
    {
      type: 'Button',
      label: 'Submission with Shares',
      url: 'divisor_basket_input_with_shares',
    },
    {
      type: 'Button',
      label: 'Submission with Notionals',
      url: 'divisor_basket_input_with_notional',
    },
  ],
  Global: [{ type: 'Button', label: 'Global Template', url: 'global_input' }],
  'Long/Short': [{ type: 'Button', label: 'Long/short Template', url: 'long_short_input' }],
};

const styleClose = {
  position: 'absolute',
  fontSize: '30px',
  fontWeight: 'bold',
  top: '-10px',
  right: '0',
  cursor: 'pointer',
  padding: '10px',
  boxSizing: 'border-box',
};

const formatDateString = dateString => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

// Verifies that a date is from today or later (and not a date in the past). For example: valid dates: today date, tomorrow date. Invalid dates: yesterday date, last month date, etc.
function isValidDateString(dateString) {
  // Check if the date string matches the format yyyy-mm-dd
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) {
    return false;
  }

  // Parse the date string into a Date object
  const date = new Date(`${dateString}T00:00:00`); // Treat as local midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today's date to local midnight

  // Check if the date is today or in the future
  return date >= today;
}

// TODO improve this with the isValidDateString
const isValidDateFormat = dateString => {
  let isValid = false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;

  if (!regex.test(dateString)) {
    isValid = false;
  }

  const date = new Date(dateString);
  isValid = date.toISOString().slice(0, 10) === dateString;

  return isValid;
};

const validatedFieldsValues = {
  currency: CURRENCIES_ARRAY,
  divisor_determination_setup: ['PREVIOUS_EOD', 'IMMEDIATELY'],
  calculation_setup: ['EOD', 'EOD_INTRADAY'],
  deployment_setup: ['EOD', 'IMMEDIATELY'],
  calculation_type: ['NET_RETURN', 'TOTAL_RETURN', 'PRICE_RETURN'],
  cash_distribution: [
    'DISTRIBUTION_ACROSS_BASKET',
    'DISTRIBUTION_ON_PAYING_SECURITY',
    'ADD_DISTRIBUTION_TO_CASH',
  ],
  application_point: ['AT_MARKET_CLOSE', 'AT_MARKET_OPEN'],
  dissemination: ['BITA Only', 'BITA + Bloomberg'],
};

const INITIAL_PAGINATION = {
  pageIndex: 0,
  pageSize: 10,
};

const validateData = [
  'divisor',
  'calculation_setup',
  'deployment_setup',
  'currency',
  'calculation_type',
  'calculation_zone',
  'cash_distribution',
  'application_point',
  'dissemination',
];

const validateDataLabel = {
  symbol: 'Symbol',
  divisor: 'Divisor',
  calculation_setup: 'Calculation frequency',
  deployment_setup: 'Launch Point',
  currency: 'Currency',
  calculation_type: 'Return calculation',
  calculation_zone: 'Calculation Zone',
  cash_distribution: 'Cash Distribution',
  application_point: 'Application Point',
  dissemination: 'Dissemination',
};

const UpdateBasket = () => {
  const authOrgPrefix = useSelector(state => state.auth?.org_prefix);
  const [files, setFiles] = React.useState('');
  const user = useSelector(state => state.auth);
  const isClearStreetAmericasUser = hasValidClearStreetAmericas(user?.userdata?.id_company);

  // Helper function to parse date from various formats (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, etc.)
  const parseDate = dateInput => {
    if (!dateInput) return null;

    // If it's already a Date object, return it
    if (dateInput instanceof Date) {
      return Number.isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // If it's a string, try to parse it
    if (typeof dateInput === 'string') {
      const trimmed = dateInput.trim();

      // Handle ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const date = new Date(trimmed);
        return Number.isNaN(date.getTime()) ? null : date;
      }

      // Handle DD/MM/YYYY or MM/DD/YYYY format
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        const parts = trimmed.split('/');
        const firstPart = parseInt(parts[0], 10);
        const secondPart = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Helper to create UTC date at midnight (no timezone issues)
        const createUTCDate = (y, m, d) => {
          return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
        };

        // If first part > 12, it must be DD/MM/YYYY (day can't be > 12 in MM/DD/YYYY)
        if (firstPart > 12) {
          const date = createUTCDate(year, secondPart, firstPart);
          if (
            !Number.isNaN(date.getTime()) &&
            date.getUTCFullYear() === year &&
            date.getUTCMonth() === secondPart - 1 &&
            date.getUTCDate() === firstPart
          ) {
            return date;
          }
        }

        // If second part > 12, it must be DD/MM/YYYY (month can't be > 12)
        if (secondPart > 12) {
          const date = createUTCDate(year, firstPart, secondPart);
          if (
            !Number.isNaN(date.getTime()) &&
            date.getUTCFullYear() === year &&
            date.getUTCMonth() === firstPart - 1 &&
            date.getUTCDate() === secondPart
          ) {
            return date;
          }
        }

        // Ambiguous case (e.g., 04/12/2025, 05/12/2025, 06/12/2025) - always prefer DD/MM/YYYY
        // Try DD/MM/YYYY first (more common in international format)
        let date = createUTCDate(year, secondPart, firstPart);
        if (
          !Number.isNaN(date.getTime()) &&
          date.getUTCFullYear() === year &&
          date.getUTCMonth() === secondPart - 1 &&
          date.getUTCDate() === firstPart
        ) {
          return date;
        }

        // Try MM/DD/YYYY as fallback only if DD/MM/YYYY didn't work
        date = createUTCDate(year, firstPart, secondPart);
        if (
          !Number.isNaN(date.getTime()) &&
          date.getUTCFullYear() === year &&
          date.getUTCMonth() === firstPart - 1 &&
          date.getUTCDate() === secondPart
        ) {
          return date;
        }

        // Final fallback - return DD/MM/YYYY interpretation
        date = createUTCDate(year, secondPart, firstPart);
        if (!Number.isNaN(date.getTime()) && date.getUTCFullYear() === year) {
          return date;
        }
      }

      // Try default Date constructor as fallback
      const date = new Date(trimmed);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  };

  // Helper function to format a Date object to YYYY-MM-DD string
  const formatDateToYYYYMMDD = date => {
    if (!date) return null;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to check if a date is a business day (Monday-Friday)
  const isBusinessDay = date => {
    if (!date) return false;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return false;
    const dayOfWeek = d.getUTCDay(); // 0 = Sunday, 6 = Saturday
    return dayOfWeek !== 0 && dayOfWeek !== 6; // Monday = 1, Friday = 5
  };

  // Helper function to get the next business day from a given date
  const getNextBusinessDay = date => {
    const d = new Date(date);
    let nextDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

    while (!isBusinessDay(nextDay)) {
      nextDay = new Date(
        Date.UTC(nextDay.getUTCFullYear(), nextDay.getUTCMonth(), nextDay.getUTCDate() + 1),
      );
    }

    return nextDay;
  };

  // Initialize effective date with today (or next business day if today is weekend)
  // Returns a local date for display
  const getInitialEffectiveDate = () => {
    // Get today's LOCAL calendar date (not UTC)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    todayLocal.setHours(0, 0, 0, 0);

    // Create UTC version for business day validation
    const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    let utcDate = utcToday;
    if (!isBusinessDay(utcToday)) {
      utcDate = getNextBusinessDay(utcToday);
    }

    // Convert UTC to local date for display
    const localDate = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
    );
    localDate.setHours(0, 0, 0, 0);
    return localDate;
  };

  const [showModal, setShowModal] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [type, setType] = useState('Long only Basket');
  const [data, setData] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isCoverageLoading, setIsCoverageLoading] = useState(false);
  const coverageLoadingCount = useRef(0);
  const setCoverageRequestLoading = inProgress => {
    coverageLoadingCount.current = Math.max(
      0,
      coverageLoadingCount.current + (inProgress ? 1 : -1),
    );
    setIsCoverageLoading(coverageLoadingCount.current > 0);
  };
  const [previousData, setPreviousData] = useState();
  const [dataBacktest, setDataBacktest] = useState([]);
  const [dataToshowOnSummary, setDataToshowOnSummary] = useState();
  const [errorReferenceData, setErrorReferenceData] = useState(false);
  const [idIndex, setIdIndex] = useState();
  const [selectBasket, setSelectBasket] = useState();
  const [filesBacktest, setFilesBacktest] = React.useState('');
  const [listBasket, setListBasket] = useState([]);
  const [listMIC, setListMIC] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState(() => getInitialEffectiveDate());
  const [constituentsDelete, setConstituentsDelete] = useState([]);
  const [showModalRequestCoverage, setShowModalRequestCoverage] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [buttonStateLoading, setButtonStateLoading] = useState({
    basket: false,
    backtest: false,
    submit: false,
    downloadTemplate: false,
  });
  const isUBSUser = authOrgPrefix === ORGANIZATION_PREFIXES.UBS;
  const [showModalRequestCoverageSuccessful, setShowModalRequestCoverageSuccessful] = useState(
    false,
  );
  const [enableDivisorOrStartingValue, setEnableDivisorOrStartingValue] = useState({
    statusDivisor: true,
    statusStarting: false,
  });
  const { token } = user;
  const [errorCalendar, setErrorCalendar] = useState(false);
  const [isActiveAnnualizedYearlyDecrement, setIsActiveAnnualizedYearlyDecrement] = useState(false);
  const [templateData, setTemplateData] = useState([]);
  const [openWithholdingModal, setOpenWithholdingModal] = useState(false);
  const [automaticallyAdjustDivisor, setAutomaticallyAdjustDivisor] = useState(true);
  const [calculationZoneList, setCalculationZoneList] = useState([]);

  const inputBasketRef = useRef(null);
  const inputBacktestRef = useRef(null);

  const history = useHistory();

  const getTemplates = () => {
    return get(URL_CONSTANTS.WITHHOLDING_TAX, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  useEffect(() => {
    if (data?.meta_reference_data?.length > 0) {
      setIsDataLoading(false);
    }
  }, [data]);

  const getNextBusinessDayUTC = date => {
    // Get today's LOCAL calendar date for comparison
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    todayLocal.setHours(0, 0, 0, 0);

    // Create UTC version of today for business day validation
    const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    // Get the selected date as local calendar date
    const selectedDate = new Date(date);
    const selectedDateLocal = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    selectedDateLocal.setHours(0, 0, 0, 0);

    // Compare LOCAL calendar dates (not UTC timestamps)
    // If selected date is before today, use today instead
    const dateToUse = selectedDateLocal < todayLocal ? todayLocal : selectedDateLocal;

    // Create UTC version of the date to use
    let result = new Date(
      Date.UTC(dateToUse.getFullYear(), dateToUse.getMonth(), dateToUse.getDate()),
    );

    // Adjust to next business day if it's a weekend
    while (result.getUTCDay() === 0 || result.getUTCDay() === 6) {
      result = new Date(
        Date.UTC(result.getUTCFullYear(), result.getUTCMonth(), result.getUTCDate() + 1),
      );
    }

    return result;
  };

  // Initialize effective date on component mount and show toast if adjusted to next business day
  useEffect(() => {
    // Get today's LOCAL calendar date (not UTC)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    todayLocal.setHours(0, 0, 0, 0);

    // Create UTC version for business day validation
    const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    if (!isBusinessDay(utcToday)) {
      const nextBusinessDayUTC = getNextBusinessDay(utcToday);
      // Convert UTC to local date for display
      const nextBusinessDayLocal = new Date(
        nextBusinessDayUTC.getUTCFullYear(),
        nextBusinessDayUTC.getUTCMonth(),
        nextBusinessDayUTC.getUTCDate(),
      );
      nextBusinessDayLocal.setHours(0, 0, 0, 0);
      setEffectiveDate(nextBusinessDayLocal);
      toast.info(
        'Today is not a business day. Effective date has been set to the next business day.',
      );
    } else {
      // Use local date for display
      setEffectiveDate(todayLocal);
    }
  }, []);

  const cleanStates = () => {
    setShowModal(false);
    setShowModal(false);
    // setData(null);
    setFiles('');
    setFilesBacktest('');

    // new states to clean
    setErrorCalendar(false);

    setShowModalDelete(false);
    setShowModalRequestCoverageSuccessful(false);
    setShowModalRequestCoverage(false);
    setShowModalConfirm(false);
    setDataBacktest([]);
    setConstituentsDelete([]);
    // setConstituentsError([]);
    setIsActiveAnnualizedYearlyDecrement(false);
    setData({});
    setType('Long only Basket');
  };

  // Add a custom row to remove the U icon in the first column
  const CustomRow = ({ entryKey, value }) => {
    return (
      <div>
        <p>
          {entryKey === 'weights' && typeof value === 'number'
            ? value.toFixed(6)
            : isFloatWithMoreThanTwoDecimals(value) || value}
        </p>
      </div>
    );
  };

  const getColumns = (valueData, removeTypeColumn = false) => {
    const columns = [
      IDENTIFIER_TYPE.ISIN,
      ...Object.keys(valueData?.[0] ?? {})
        .map(column => (column === 'exchange' ? 'MIC' : column))
        .filter(
          item =>
            item !== IDENTIFIER_TYPE.ISIN &&
            (data?.identifier_type === IDENTIFIER_TYPE.ISIN || item !== IDENTIFIER_TYPE.SEDOL),
        )
        .filter(item => !(removeTypeColumn && item === 'type')),
    ];

    if (isUBSUser) {
      const element = IDENTIFIER_TYPE.SEDOL;
      const index = columns.indexOf(element);
      if (index !== -1) {
        columns.splice(index, 1);
        columns.unshift(element);
      }
    }
    return columns;
  };

  const getColumnsUpdateTable = valueData => {
    const columns = Object.keys(valueData?.[0] ?? {}).map(column =>
      column === 'exchange' ? 'MIC' : column,
    );

    if (isUBSUser && data?.identifier_type !== IDENTIFIER_TYPE.ISIN) {
      const element = IDENTIFIER_TYPE.SEDOL;
      const index = columns.indexOf(element);
      if (index !== -1) {
        columns.splice(index, 1);
        columns.unshift(element);
      }
    } else if (data?.identifier_type === IDENTIFIER_TYPE.ISIN) {
      const index = columns.indexOf(IDENTIFIER_TYPE.SEDOL.toUpperCase());
      if (index !== -1) {
        columns.splice(index, 1);
      }
    }
    return columns;
  };

  const transformRow = (row, orderColumns) => {
    const newRow = {
      isin: row.isin,
      sedol: row.sedol || '-',
      MIC: row.exchange,
      ...Object.fromEntries(
        Object.entries(row).filter(
          ([key]) => key !== IDENTIFIER_TYPE.SEDOL && key !== IDENTIFIER_TYPE.ISIN,
        ),
      ),
    };
    return orderColumns.reduce((acc, column) => {
      acc[column] = newRow[column] ?? '-';

      return acc;
    }, {});
  };

  const renderRows = valueData => {
    const orderColumns = getColumns(selectBasket?.meta_reference_data);
    const dataRows = valueData ?? [];
    return dataRows.map(row => {
      const newRow = transformRow(row, orderColumns);
      return newRow;
    });
  };

  const renderRowsUpdateTable = valueData => {
    const dataRows = valueData ?? [];
    if (isUBSUser) {
      return dataRows.map(row => {
        if (row.sedol) {
          return { sedol: row.sedol, ...row, MIC: row.exchange };
        }
        return { ...row, MIC: row.exchange };
      });
    }
    return dataRows.map(row => ({ sedol: '-', ...row, MIC: row.exchange }));
  };

  useEffect(() => {
    getMIC(token)
      .then(rsp => {
        setListMIC(Object.values(rsp?.data?.data));
      })
      .catch(error => {
        toastInterceptor(error);
      });
    getBasket(token)
      .then(rsp => {
        setListBasket(Object.values(rsp?.data?.data));
      })
      .catch(error => {
        toastInterceptor(error);
      });

    getTemplates()
      .then(rsp => {
        const dataTax = [
          ...rsp?.data?.data,
          {
            withholding_tax_name: 'BITA Withholding Taxes',
            id: 'BITA Withholding Taxes',
            custom: 'false',
          },
        ];
        setTemplateData(dataTax);
      })
      .catch(error => {
        toastInterceptor(error);
      });
  }, []);

  const processDataArray = () => {
    if (data?.meta_reference_data?.length > 0 && previousData?.meta_reference_data?.length > 0) {
      const result = [];
      const oldData = {};

      // eslint-disable-next-line no-unused-expressions
      previousData?.meta_reference_data?.forEach(item => {
        oldData[item?.isin] = item;
      });
      // eslint-disable-next-line no-unused-expressions
      data?.meta_reference_data.forEach(item => {
        let typeOfModification = 'no_change';
        const oldInstrument = oldData?.[item.isin];
        if (oldInstrument) {
          if (oldInstrument?.exchange !== item.exchange || oldInstrument?.shares !== item.shares) {
            typeOfModification = 'Update';
          }
          delete oldData?.[item.isin];
        } else {
          typeOfModification = 'Addition';
        }

        if (typeOfModification !== 'no_change') {
          let entry;
          if (isUBSUser) {
            entry = {
              SEDOL: item?.sedol ?? '-',
              ISIN: item?.isin ?? '-',
              ticker: item.ticker ?? '-',
              'OLD MIC': oldInstrument?.exchange ?? '-',
              'NEW MIC': item.exchange ?? '-',
              'OLD SHARES': oldInstrument?.shares ?? '-',
              'NEW SHARES': item.shares ?? '-',
              type: typeOfModification,
            };
          } else {
            entry = {
              ISIN: item?.isin ?? '-',
              SEDOL: item?.sedol ?? '-',
              ticker: item.ticker ?? '-',
              'OLD MIC': oldInstrument?.exchange ?? '-',
              'NEW MIC': item.exchange ?? '-',
              'OLD SHARES': oldInstrument?.shares ?? '-',
              'NEW SHARES': item.shares ?? '-',
              type: typeOfModification,
            };
          }
          result.push(entry);
        }
      });

      Object.keys(oldData).forEach(key => {
        let entry;
        if (isUBSUser) {
          entry = {
            SEDOL: oldData[key]?.sedol ?? '-',
            ISIN: oldData[key]?.isin ?? '-',
            ticker: oldData[key]?.ticker ?? oldData[key]?.isin,
            'OLD MIC': oldData[key]?.exchange ?? '-',
            'NEW MIC': '-',
            'OLD SHARES': oldData[key]?.shares ?? '-',
            'NEW SHARES': '-',
            type: 'Delete',
          };
        } else {
          entry = {
            ISIN: oldData[key]?.isin ?? '-',
            SEDOL: oldData[key]?.sedol ?? '-',
            ticker: oldData[key]?.ticker ?? oldData[key]?.isin,
            'OLD MIC': oldData[key]?.exchange ?? '-',
            'NEW MIC': '-',
            'OLD SHARES': oldData[key]?.shares ?? '-',
            'NEW SHARES': '-',
            type: 'Delete',
          };
        }
        result.push(entry);
      });
      setDataToshowOnSummary([...result]);
    }
  };

  const resetBasketData = () => {
    setData(null);
    setFiles('');
    setSelectBasket({ value: '' });
    setFilesBacktest('');
  };

  const handleSuccess = backtestError => {
    setShowModalConfirm(false);

    if (backtestError) {
      toast(
        'Your basket has been processed successfully but your backtest has not been processed. Please contact support',
      );
    } else {
      toast('Your Basket has been processed successfully');
    }
    setShowModal(true);
    setIdIndex(selectBasket?.id);
    if (inputBacktestRef && inputBacktestRef.current) {
      inputBacktestRef.current.value = '';
    }
    getBasket(token)
      .then(resp => {
        setListBasket(Object.values(resp?.data?.data));
      })
      .catch(error => {
        toastInterceptor(error);
      });
  };

  const existsCashConstituentsInMetadata = () =>
    data?.meta_reference_data?.some(instrument => instrument?.exchange === 'CASH') || false;

  const handleSubmitButton = async () => {
    const generateBody = () => {
      // Exclude fields from data
      const {
        metadata_effective_date,
        m_index_eod_execution_group,
        calculation_frequency,
        count_reference_data,
        count_metadata,
        id_m_index_parent,
        additional_settings,
        meta_reference_data,
        effective_date,
        launch_date,
        eod_calculation_time,
        ...filteredRest
      } = data || {};

      const updateData = {
        ...filteredRest,
        withholding_tax_id:
          !data?.withholding_tax_id || data?.withholding_tax_id === 'BITA withholding taxes'
            ? null
            : data?.withholding_tax_id,
        m_withholding_tax_id:
          !data?.withholding_tax_id || data?.withholding_tax_id === 'BITA withholding taxes'
            ? null
            : data?.withholding_tax_id,
        basket_id: selectBasket.id,
        symbol: data.symbol,
        divisor: Number(data?.divisor),
        decrement_basket: {
          annualized_daily_decrement: isActiveAnnualizedYearlyDecrement,
          decrement_value_percentage: isActiveAnnualizedYearlyDecrement
            ? Number(data?.percentage_value)
            : 0,
        },
        divisor_determination_setup: data?.divisor_determination_setup?.replace(' ', '_'),
        metadata_effective_date: effectiveDate
          ? formatDateToYYYYMMDD(effectiveDate)
          : data?.effective_date ?? generateEffectiveDate(data?.calculation_setup),
        calculation_type: data.calculation_type.toUpperCase(),
        calculation_setup: data.calculation_setup,
        metadata: data.meta_reference_data,
        cash_constituent: existsCashConstituentsInMetadata(data?.symbol)
          ? {
              interest_rate_on_cash: Number(data?.cash_constituent?.interest_rate_on_cash) || 0,
            }
          : null,
      };

      if (automaticallyAdjustDivisor) {
        delete updateData.divisor;
      }

      if (data?.calculation_type === 'PRICE_RETURN') {
        delete updateData.cash_distribution;
        delete updateData.application_point;
      }

      if (
        data?.calculation_setup === 'EOD_INTRADAY' &&
        selectBasket?.calculation_frequency === 'EOD'
      ) {
        updateData.intraday_start_day = formatDateToYYYYMMDD(effectiveDate);
      }

      delete updateData.meta_reference_data;

      if (updateData.additional_settings) {
        delete updateData.additional_settings.annualized_daily_decrement;
        delete updateData.additional_settings.decrement_value_percentage;
      }

      delete updateData.percentage_value;

      return {
        basket_update: {
          ...updateData,
        },
      };
    };
    const body = {
      data: [generateBody()],
    };
    try {
      const bulkResponse = await updateBulkBasket(user.token, body);
      const processedBasketsList = Object.entries(bulkResponse?.data?.data).map(
        ([symbol, basket]) => ({
          symbol,
          message: basket?.basket_update?.message,
          status: basket?.basket_update?.status,
        }),
      );
      const basketUpdate = processedBasketsList[0];
      if (basketUpdate?.status !== 'updated') {
        toastInterceptor(basketUpdate?.message);
      } else if (filesBacktest) {
        const formData = new FormData();
        formData.append('template', filesBacktest);
        uploadBacktest(token, formData, basketUpdate.symbol)
          .then(handleSuccess(false))
          .catch(error => {
            handleSuccess(true);
          });
      } else {
        handleSuccess();
      }
    } catch (error) {
      toast.error(
        'Failed to update basket. Please check your network connection or try again later.',
      );
    } finally {
      setButtonStateLoading({ ...buttonStateLoading, submit: false });
    }
  };

  const existRequestCoverage = () => {
    let coverage = false;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < data?.meta_reference_data?.length; i++) {
      if (
        data?.meta_reference_data[i]?.type &&
        data?.meta_reference_data[i]?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE
      ) {
        coverage = true;
        break;
      }
    }
    return coverage;
  };

  const BacktestTable = () => {
    const renderTable = () => {
      const columns = Object.keys(dataBacktest?.[0] ?? {});
      const rows = dataBacktest.slice(1);

      return (
        <div style={{ height: '340px' }}>
          <div style={{ border: 'none', marginBottom: '0px' }} className={tableStyles.tableTitle}>
            Backtest Data Preview
          </div>
          <BitaTable
            data={rows}
            search={false}
            showFields={columns}
            height="300px"
            showOnly={10}
            pagination
            selectAction={() => {}}
            CustomRowComponent={({ value }) => (
              <div>
                <p style={{ paddingRight: '20px' }}>{value}</p>
              </div>
            )}
          />
        </div>
      );
    };
    return <div className={style.contentWidget}>{renderTable()}</div>;
  };

  const BasketTable = ({ titleBasketTable, deleteBasketTable, buttonsBasketTable, noScroll }) => {
    const renderTable = () => {
      if (type === 'Long only Basket' && data.meta_reference_data) {
        const updatedRows = data.meta_reference_data;
        const columns = Object.keys(data.meta_reference_data?.[0] ?? {});

        return (
          <div className="contentTableBasketPreview">
            {titleBasketTable && (
              <div style={{ border: 'none', marginBottom: '0px' }}>Basket Data Preview</div>
            )}
            {titleBasketTable && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h1>General file information section</h1>
              </div>
            )}
            {noScroll ? (
              <> </>
            ) : (
              <BitaTable
                id="basket"
                data={updatedRows ?? []}
                height="100%"
                pagination
                style={{ padding: '0' }}
                search={false}
                scroll={!noScroll}
                showFields={deleteBasketTable ? [...(columns ?? [])] : columns ?? []}
                selectAction={() => {}}
                editButton={false}
                CustomRowComponent={({ data: dataValue, entryKey, value }) => (
                  <div
                    className="custom-row-container"
                    style={{ width: entryKey === 'ticker' ? '100px' : '' }}
                  >
                    <p className={`${entryKey === 'type' ? 'info-icon-live' : ''}`}>
                      {entryKey === 'type' && deleteBasketTable ? (
                        value === TYPE_OF_MODIFICATION.WITH_COVERAGE ? (
                          <FaCheckSquare className="icon-info" />
                        ) : (
                          <BiSolidError className="icon-error" />
                        )
                      ) : entryKey === IDENTIFIER_TYPE.ISIN && deleteBasketTable ? (
                        <div style={{ display: 'flex' }}>
                          <BitaCheckbox
                            checked={constituentsDelete.includes(
                              dataValue?.isin ?? dataValue?.sedol,
                            )}
                            handleChange={checked => {
                              if (checked) {
                                setConstituentsDelete([
                                  ...constituentsDelete,
                                  dataValue?.isin ?? dataValue?.sedol,
                                ]);
                              } else {
                                setConstituentsDelete(
                                  constituentsDelete.filter(
                                    item => item !== (dataValue?.isin ?? dataValue?.sedol),
                                  ),
                                );
                              }
                            }}
                          />
                          {value}
                        </div>
                      ) : (
                        value ?? '-'
                      )}
                    </p>
                    {entryKey === 'type' && value !== TYPE_OF_MODIFICATION.WITH_COVERAGE && (
                      <div className="tooltip error-tooltip">
                        <div>
                          <BiX className="tooltip-icon icon-error" />
                        </div>
                        <span>
                          This instrument is not under the BITA Calculation platform pricing
                          coverage. You can request the coverage using the button below &quot;
                          Request Coverage &quot;. We will receive the request, process it, and
                          communicate with you via e-mail as soon as possible.
                        </span>
                      </div>
                    )}
                    {entryKey === 'type' && value === TYPE_OF_MODIFICATION.WITH_COVERAGE && (
                      <div className="tooltip success-tooltip">
                        <div>
                          <BiCheck className="tooltip-icon icon-info" />
                        </div>
                        <span>The input has been successfully validated</span>
                      </div>
                    )}
                  </div>
                )}
                // eslint-disable-next-line no-shadow
              />
            )}
          </div>
        );
      }

      if (type === 'Decrement Index') {
        return <></>;
      }
      return null;
    };

    return <div className={titleBasketTable ? '' : style.contentWidget}>{renderTable()}</div>;
  };

  const handleChange = (name, value) => {
    if (name === 'application_point') {
      setData({
        ...data,
        additional_settings: {
          ...data?.additional_settings,
          cash_distribution_application_point: value,
        },
        [name]: value,
      });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  const validateFields = (key, field) => {
    let acceptedValues;
    if (!key || !field) return false;
    if (key === 'withholding_tax_id') {
      acceptedValues = templateData?.some(item => item.id === field);
      return acceptedValues;
    }
    if (key === 'calculation_eod_time') {
      return true;
    }
    acceptedValues = validatedFieldsValues[key];
    return !!acceptedValues?.includes(field);
  };

  const validarData = (dataSend, list) => {
    if (dataSend.calculation_setup === 'EOD') {
      list = list.filter(element => element !== 'deployment_setup');
    }
    return list.every(propertyValidate => {
      if (propertyValidate === 'divisor') {
        // Special case for divisor: check if it exists and is a number greater than 0
        if (automaticallyAdjustDivisor) {
          return true;
        }
        return propertyValidate in dataSend && Number(dataSend[propertyValidate]) > 0;
      }
      if (propertyValidate === 'cash_distribution' || propertyValidate === 'application_point') {
        if (dataSend.calculation_type === 'PRICE_RETURN') {
          return true;
        }
        return (
          propertyValidate in dataSend &&
          typeof dataSend[propertyValidate] === 'string' &&
          dataSend[propertyValidate].trim() !== ''
        );
      }
      // For all other properties, keep the original checks
      return (
        propertyValidate in dataSend &&
        typeof dataSend[propertyValidate] === 'string' &&
        dataSend[propertyValidate].trim() !== ''
      );
    });
  };

  const handleConstituentToggle = (value, checked = null) => {
    setConstituentsDelete(prev => {
      // Helper function for comparing items
      const isSameItem = (item1, item2) => {
        if (item1.isin && item2.isin) return item1.isin === item2.isin;
        if (item1.sedol && item2.sedol) return item1.sedol === item2.sedol;
        return false;
      };

      // Determine whether we should add or remove
      const shouldAdd = checked !== null ? checked : !prev.some(item => isSameItem(item, value));

      if (shouldAdd) {
        // Avoid duplicates
        if (prev.some(item => isSameItem(item, value))) {
          return prev;
        }
        return [...prev, value];
      }
      // Remove the item
      return prev.filter(item => !isSameItem(item, value));
    });
  };

  const fieldsValidations = () => {
    const { statusDivisor, statusStarting } = enableDivisorOrStartingValue;

    try {
      if (!data) return false;

      return (
        data?.meta_reference_data &&
        validarData(
          data,
          validateData.filter(
            item => !(statusStarting && data.divisor_determination_setup && item === 'divisor'),
          ),
        ) &&
        (data.calculation_type !== 'PRICE_RETURN'
          ? validateFields('cash_distribution', data?.cash_distribution)
          : true) &&
        (data.calculation_type !== 'PRICE_RETURN'
          ? validateFields('application_point', data?.application_point)
          : true) &&
        validateFields('calculation_type', data?.calculation_type) // This is return type
      );
    } catch (error) {
      console.error('Validation error:', error);
      return false;
    }
  };

  // Function to validate if either the divisor or starting value is valid
  const isDivisorOrStartingValueValid = () => {
    const { divisor } = data || {};
    const { statusDivisor } = enableDivisorOrStartingValue;

    return divisor > 0 && statusDivisor;
  };

  const interestCashValid =
    !data?.cash_constituent ||
    !existsCashConstituentsInMetadata() ||
    (data?.cash_constituent?.interest_rate_on_cash != null &&
      parseFloat(data?.cash_constituent?.interest_rate_on_cash.toString().replace(/,/g, '')) >= 0 &&
      data?.cash_constituent?.interest_rate_on_cash !== '');

  const checkForErrors = constituents => {
    return constituents.some(item => item.shares === 0);
  };

  const applyButtonValidations = () => {
    const hasReferenceDataError = errorReferenceData;
    const hasValidBasketData =
      selectBasket && (data?.meta_reference_data || dataBacktest.length > 0);
    const hasRequestCoverage = existRequestCoverage();
    const hasExchangeData = data?.meta_reference_data?.[0]?.exchange || false;
    const areFieldsValid = fieldsValidations();
    const currencyValidations = data?.currency && validateFields('currency', data?.currency);
    const hasMetadataExists = data?.meta_reference_data?.length > 0;
    const hasDivisorDeterminationSetup =
      !automaticallyAdjustDivisor || data?.divisor_determination_setup;
    // If a file is present, perform additional checks
    if (files?.name) {
      return (
        !hasDivisorDeterminationSetup ||
        !interestCashValid ||
        hasReferenceDataError ||
        !hasMetadataExists ||
        !hasValidBasketData ||
        hasRequestCoverage ||
        !hasExchangeData ||
        !areFieldsValid ||
        !currencyValidations
      );
    }

    // Perform basic checks when no file is present
    return (
      !hasDivisorDeterminationSetup ||
      !interestCashValid ||
      !areFieldsValid ||
      !currencyValidations ||
      hasReferenceDataError ||
      !hasMetadataExists
    );
  };

  const handleApply = () => {
    // Ensure data.effective_date is synced with effectiveDate state before showing modal
    if (effectiveDate && data) {
      setData({
        ...data,
        effective_date: formatDateToYYYYMMDD(effectiveDate),
      });
    }
    setShowModalConfirm(true);
    processDataArray();
  };

  const getCalculationSetup = item => {
    if (item?.calculate && item?.intraday) {
      return 'EOD_INTRADAY';
    }
    return 'EOD';
  };

  const calculationZones = async () => {
    const calculationZoneData = await handleCalculationZones(token);
    setCalculationZoneList(calculationZoneData);
  };

  const calculationZoneMap = useMemo(() => {
    if (!Array.isArray(calculationZoneList)) return new Map();

    return new Map(calculationZoneList.map(zone => [zone.name, zone]));
  }, [calculationZoneList]);

  const calculationZoneOptions = useMemo(() => {
    if (!Array.isArray(calculationZoneList)) return [];

    return calculationZoneList.map(zone => ({
      id: zone.name,
      value: zone.public_name ?? zone.name,
    }));
  }, [calculationZoneList]);

  const filteredCalculationZones = useMemo(() => {
    if (!calculationZoneOptions.length) return [];

    const companyId = user?.userdata?.id_company;
    const email = user?.userdata?.email;

    const isSpecialUser = hasValidUBSAPACOnly(companyId) || hasValidBitaDomain(email);
    const isMarketVectorAll = hasValidMarketVectorOrgAll(companyId);
    const extraMarketVectorCalculationZones = CALCULATION_ZONE_FOR_MARKET_VECTOR;

    return calculationZoneOptions.filter(item => {
      if (item.id === EASTERN_APAC_NAME && !isSpecialUser) return false;
      if (!isMarketVectorAll && extraMarketVectorCalculationZones.includes(item.id)) return false;
      return true;
    });
  }, [calculationZoneOptions, user]);

  useEffect(() => {
    calculationZones();
  }, []);

  const handleClickDownloadTemplate = async () => {
    setButtonStateLoading({ ...buttonStateLoading, downloadTemplate: true });
    await handleDownloadTemplate(user, data);
    setButtonStateLoading({ ...buttonStateLoading, downloadTemplate: false });
  };

  const handleRequestCoverageSubmit = () => {
    const body = buildRequestCoverageBody({
      data,
      user,
      withCoverageType: 'with_coverage',
    });
    setShowModalRequestCoverage(false);
    coverageRequest(token, body)
      .then(rsp => {
        if (rsp?.data?.data.email_sent) {
          setShowModalRequestCoverageSuccessful(true);
        } else {
          toast.error('We had a problem processing your request');
        }
        setShowModalRequestCoverage(false);
      })
      .catch(error => {
        toast.error('We had a problem processing your request');
      });
  };

  const updateDataDefaults = (basketFileData, selectedBasket) => {
    const dataFile = {
      ...basketFileData,
      // Ensure symbol remains the same as the selected basket
      symbol: selectedBasket?.symbol,
      meta_reference_data: basketFileData?.meta_reference_data
        ? orderByType(basketFileData?.meta_reference_data)
        : selectedBasket?.meta_reference_data,
      calculation_zone:
        basketFileData?.calculation_zone?.toUpperCase() || selectedBasket?.calculationZone,
      divisor_determination_setup: basketFileData?.divisor_determination_point || 'PREVIOUS_EOD',
      dissemination: previousData?.dissemination,
      divisor: basketFileData?.divisor || 0,

      // Required fields here
      currency: validateFields('currency', basketFileData?.currency)
        ? basketFileData?.currency
        : selectedBasket?.currency,

      // Optional fields
      name: basketFileData?.name || selectedBasket?.name, // add name from file, if not, then add name from the current basket
      description: basketFileData?.description || selectedBasket?.description, // add description from file, if not, then add name from the current basket
      cash_distribution:
        basketFileData?.cash_distribution ||
        selectedBasket?.additional_settings?.m_cash_distribution_type?.name,
      application_point:
        basketFileData?.application_point ??
        selectedBasket?.additional_settings?.cash_distribution_application_point,
      calculation_type:
        basketFileData?.calculation_type?.toUpperCase() ||
        selectedBasket?.calculation_type?.toUpperCase(),
      calculation_setup: basketFileData?.calculation_setup || getCalculationSetup(selectedBasket),
      deployment_setup: basketFileData?.deployment_setup || 'EOD',
    };

    if (dataFile?.starting_value) {
      // remove starting value if comes from file
      delete dataFile.starting_value;
    }

    setData(dataFile);
  };

  return (
    <div>
      {/* Live Basket(s) selector and Basket Data Preview */}
      <div className={style.compositionPortfolio}>
        <div className={style.compositionPortfolioTitle}>Live Basket(s)</div>
        <div className={styles.contentDataUpload}>
          <div style={{ marginRight: 12 }}>
            <div className={style.container}>
              <div style={{ width: '235px', paddingLeft: '5%', paddingBottom: '5%' }}>
                <div className={style.subtitle} style={{ paddingBottom: '10%' }}>
                  Basket
                </div>
                <BitaSearchBar
                  forceStopLoading
                  key="Baskets"
                  placeholder="Live Basket(s)"
                  searchOptions={getBasketSearchOptions(listBasket)}
                  value={selectBasket?.value}
                  onChange={async selected => {
                    setFiles('');
                    const basketData = await getBasketById(token, selected?.value);
                    if (Object.keys(basketData?.data?.data).length === 0) {
                      toast.error(
                        'No data was found for the selected basket. Please try again later or contact support if the issue persists.',
                      );
                      setSelectBasket({ value: '' });
                      setData({});
                      return;
                    }
                    setSelectBasket(basketData?.data.data);

                    const updatedItem = {
                      ...basketData.data.data,
                      divisor: basketData?.data?.data?.divisor || 0,
                      deployment_setup: 'EOD',
                      calculation_type:
                        basketData?.data?.data?.calculation_type?.toUpperCase() || '',
                      cash_distribution:
                        basketData?.data?.data?.additional_settings?.m_cash_distribution_type
                          ?.name || '',
                      application_point:
                        basketData?.data?.data?.additional_settings
                          ?.cash_distribution_application_point || '',
                      calculation_zone:
                        basketData?.data?.data?.additional_settings?.m_index_calculation_zone
                          ?.name || '',
                      calculation_setup: getCalculationSetup(basketData?.data?.data),
                      dissemination: basketData?.data?.data?.bloomberg_id
                        ? 'BITA + Bloomberg'
                        : 'BITA Only',
                      divisor_determination_setup:
                        basketData?.data?.data?.divisor_determination_setup || 'PREVIOUS_EOD',

                      cash_constituent:
                        basketData?.data?.data?.additional_settings?.cash_constituent,
                      withholding_tax_id:
                        basketData?.data?.data?.additional_settings?.withholding_tax_id ??
                        basketData?.data?.data?.withholding_tax_id,
                      percentage_value:
                        basketData?.data?.data?.additional_settings?.decrement_value_percentage &&
                        basketData?.data?.data?.additional_settings?.decrement_value_percentage !==
                          0
                          ? basketData?.data?.data?.additional_settings
                              ?.decrement_value_percentage * 100
                          : 0,
                    };

                    setIsActiveAnnualizedYearlyDecrement(
                      basketData?.data?.data?.additional_settings?.annualized_daily_decrement ??
                        false,
                    );
                    /* delete updatedItem.additional_settings; */
                    delete updatedItem.calculation_formula;
                    delete updatedItem.type;
                    delete updatedItem.asset_class;
                    delete updatedItem.id_m_index_parent;
                    delete updatedItem.intraday;
                    delete updatedItem.calculate;
                    delete updatedItem.launch_date;
                    setData(updatedItem); // Update the state with the new object
                    setPreviousData(updatedItem);
                  }}
                />
              </div>
            </div>
          </div>

          {selectBasket?.id && (
            <div className={styles.contentUpdateBasket}>
              <div className={tableStyles.tableTitle}>Basket Data Preview</div>
              <div
                className={tableStyles.tableContainer}
                style={{ display: 'flex', width: '100%' }}
              >
                <div className={style.contentNoMetadata} style={{ width: '28%' }}>
                  <div className={style.tableNoMetadata}>
                    <div style={{ display: 'flex' }}>
                      <div>Ticker: </div> <div>{selectBasket?.symbol}</div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>Divisor: </div> <div>{selectBasket?.divisor}</div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>Timezone: </div>{' '}
                      <div>
                        {getZoneValue(
                          calculationZoneMap,
                          data,
                          CALCULATION_ZONE_CONFIG.CALC_TIMEZONE,
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>Calculation Start: </div>{' '}
                      <div>
                        {getZoneValue(
                          calculationZoneMap,
                          data,
                          CALCULATION_ZONE_CONFIG.CALC_OPEN_TIME,
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>Calculation End: </div>{' '}
                      <div>
                        {getZoneValue(
                          calculationZoneMap,
                          data,
                          CALCULATION_ZONE_CONFIG.CALC_CLOSE_TIME,
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>Start Week Day: </div>{' '}
                      <div>
                        {getZoneValue(
                          calculationZoneMap,
                          data,
                          CALCULATION_ZONE_CONFIG.CALC_START_WEEK_DAY,
                          true,
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>End Week Day: </div>{' '}
                      <div>
                        {getZoneValue(
                          calculationZoneMap,
                          data,
                          CALCULATION_ZONE_CONFIG.CALC_END_WEEK_DAY,
                          true,
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div>Calculation EOD Time: </div>{' '}
                      <div>
                        {getZoneValue(
                          calculationZoneMap,
                          data,
                          CALCULATION_ZONE_CONFIG.CALC_EOD_TIME,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Pre visualization table */}
                <div style={{ width: '72%' }} className="tableUpdateBasket tablePreview">
                  <BitaTable
                    id="basket"
                    data={renderRows(selectBasket?.meta_reference_data)}
                    search={false}
                    pagination
                    showFields={getColumns(selectBasket?.meta_reference_data, true)}
                    selectAction={() => {}}
                    CustomRowComponent={CustomRow}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Basket Composition/Portfolio */}
      <div className={style.compositionPortfolio}>
        <div className={style.compositionPortfolioTitle}>Update Basket Composition/Portfolio</div>

        {/* Templates and Basket Data Preview */}
        <div className={styles.contentDataUpload}>
          <div>
            <div className={style.container}>
              <div className={style.contentTemplateUpload}>
                <div className={style.subtitle}>TEMPLATES</div>
                <BitaButton
                  primaryWhite
                  width="160px"
                  height="35px"
                  onClick={handleClickDownloadTemplate}
                  disabled={!selectBasket?.symbol}
                  className={
                    buttonStateLoading.downloadTemplate ? style.downloadTemplateButtonLoading : ''
                  }
                >
                  {buttonStateLoading.downloadTemplate ? (
                    <ClipLoader size={16} color="#2962FF" />
                  ) : (
                    'Basket Constituents'
                  )}
                </BitaButton>
                {Object.values(basketButton[type] ?? {}).map(button => (
                  <BitaButton
                    primaryWhite
                    width="160px"
                    height="35px"
                    onClick={() => {
                      FileSaver.saveAs(
                        `${process.env.PUBLIC_URL}/files/${button?.url}.csv`,
                        `${button?.url}.csv`,
                      );
                    }}
                  >
                    {button?.label}
                  </BitaButton>
                ))}
              </div>

              <div className={style.dataUpload}>
                <div className={style.contentTemplate}>
                  <div className={style.subtitle}>PORTFOLIO UPLOADER</div>

                  <input
                    type="file"
                    onChange={event => {
                      if (selectBasket) {
                        cleanStates();
                        setFiles(event.target.files[0]); // Basket file
                      }
                    }}
                    ref={inputBasketRef}
                    name="file"
                    id="file"
                    className={style.inputfile}
                    disabled={!selectBasket?.symbol}
                    style={{ display: 'none' }} // Hide the default file input
                  />
                  <label
                    htmlFor="file"
                    className={!selectBasket?.symbol ? style.portfolioUploaderButtonDisabled : ''}
                  >
                    Select file
                  </label>

                  <BitaButton
                    disabled={!files || buttonStateLoading.basket}
                    onClick={() => {
                      if (files && !isValidExcelFile(files)) {
                        toast.error(
                          'There was an error on the upload process. Please try again later or contact support.',
                        );
                        setButtonStateLoading({ ...buttonStateLoading, basket: false });
                        return;
                      }
                      setButtonStateLoading({ ...buttonStateLoading, basket: true });
                      setIsDataLoading(true);
                      const formData = new FormData();
                      formData.append('template', files);

                      if (type === 'Long only Basket') {
                        formData.append('type', 'basket');
                      } else {
                        formData.append('type', type.replace('/', '_'));
                        // formData.append(
                        //   'type',
                        //   type.includes('Bottom') ? 'global' : type.replace('/', '_'),
                        // );
                      }
                      setData({});
                      setDataBacktest([]);
                      setFilesBacktest('');
                      if (files) {
                        uploadFileSettings(token, formData)
                          .then(rsp => {
                            // Implementation like Single Basket
                            toast('Your file has been processed successfully');
                            // const constituents = Object.keys(rsp.data.data?.metadata.awf);
                            // const constituentsWithError = constituents.filter(
                            //   (constituyente, index) => index % 3 === 0,
                            // );
                            // setConstituentsError(constituentsWithError);

                            if (existRequestCoverageWithData(rsp.data.data?.meta_reference_data)) {
                              toast.warning('Some instruments are not covered');
                            }

                            updateDataDefaults(rsp.data.data, selectBasket);
                            inputBasketRef.current.value = '';
                          })
                          .catch(error => {
                            setIsDataLoading(false);
                            updateDataDefaults({}, selectBasket);
                            toastInterceptor(handleError(error));
                          })
                          .finally(() => {
                            setButtonStateLoading({ ...buttonStateLoading, basket: false });
                          });
                      } else {
                        setIsDataLoading(false);
                        toast.error(
                          'Please upload a portfolio template with a different file name.',
                        );
                        setButtonStateLoading({ ...buttonStateLoading, basket: false });
                      }
                    }}
                    primary
                    width="160px"
                    height="35px"
                  >
                    {buttonStateLoading.basket ? <BitaSpinner pointers /> : 'Upload File'}
                  </BitaButton>
                </div>
                <p className={style.nameFile}>{files && `(${files.name})`}</p>
              </div>
              {data?.shares_determination_point && (
                <div className={styles.contentSharesDetermination}>
                  <div className={styles.subtitleWhite}>SHARES - PRICING DETERMINATION POINT</div>
                  <div className={styles.sharesDeterminationDropdown}>
                    <BitaSelect
                      selected={data?.shares_determination_point}
                      label={SHARES_DETERMINATION_POINT[data?.shares_determination_point]}
                      data={[]}
                      handleSelection={value => {}}
                    />
                  </div>
                </div>
              )}

              <div style={{ marginLeft: '4%', width: '30%' }}>
                <BitaCalendarDate
                  title="Effective Date"
                  select={value => {
                    const utcDate = getNextBusinessDayUTC(value);
                    // Convert UTC to local date for display
                    const localDate = new Date(
                      utcDate.getUTCFullYear(),
                      utcDate.getUTCMonth(),
                      utcDate.getUTCDate(),
                    );
                    localDate.setHours(0, 0, 0, 0);
                    setEffectiveDate(localDate);
                    // Update data.effective_date to keep it in sync for modal display
                    if (data) {
                      setData({
                        ...data,
                        effective_date: formatDateToYYYYMMDD(localDate),
                      });
                    }
                  }}
                  maxDate={new Date('3000-12-12')}
                  minDate={new Date()}
                  date={effectiveDate}
                />
              </div>
            </div>
          </div>

          {/*   {data && (
            <div className="tableUpdateBasket">
              <div className={tableStyles.tableTitle}>Basket Data Preview</div>
              <BitaTable
                data={renderRows(data)}
                search={false}
                showFields={getColumns(data)}
                selectAction={() => {}}
                CustomRowComponent={CustomRow}
              />
            </div>
          )} */}

          <div style={{ padding: '0px 20px ' }}>
            {data?.meta_reference_data && (
              <div
                className="contentTableBasketPreview"
                style={{ marginTop: '0px', paddingTop: '0px' }}
              >
                <div className="headerTable">
                  <div style={{ border: 'none', marginBottom: '0px' }}>Basket Data Preview</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <h1>General file information section</h1>
                  </div>
                </div>
                {data && (
                  <div style={{ height: '600px' }}>
                    <div>
                      <EditTable
                        key={`table-update-basket-${data.meta_reference_data?.length}`}
                        basket={selectBasket.symbol}
                        rows={data.meta_reference_data}
                        listMic={listMIC}
                        calculationSetup={data.calculation_setup}
                        setMetadata={setData}
                        hiddenColumn={
                          data?.identifier_type === IDENTIFIER_TYPE.ISIN ||
                          isClearStreetAmericasUser
                            ? [IDENTIFIER_TYPE.SEDOL]
                            : []
                        }
                        constituentsDelete={constituentsDelete}
                        setConstituentsDelete={(value, checked) =>
                          handleConstituentToggle(value, checked)
                        }
                        allDelete={value => {
                          setConstituentsDelete(value);
                        }}
                        setErrorReferenceData={setErrorReferenceData}
                        isCoverageLoading={isCoverageLoading}
                      />
                    </div>
                  </div>
                )}

                {data && (
                  <BitaButton
                    disabled={!existRequestCoverage()}
                    primary
                    width="160px"
                    style={{ marginRight: 'auto', marginTop: '20px' }}
                    height="35px"
                    onClick={() => setShowModalRequestCoverage(true)}
                  >
                    Request Coverage
                  </BitaButton>
                )}

                {data && (
                  <BitaButton
                    disabled={constituentsDelete.length === 0}
                    primary
                    width="160px"
                    style={{ marginLeft: '20px', marginTop: '20px' }}
                    height="35px"
                    onClick={() => {
                      setShowModalDelete(true);
                    }}
                  >
                    Delete Constituent
                  </BitaButton>
                )}
                {data && (
                  <BitaButton
                    primary
                    width="160px"
                    style={{ marginLeft: '20px', marginTop: '20px' }}
                    height="35px"
                    onClick={() => {
                      setShowModalAdd(true);
                    }}
                  >
                    Add Instrument
                  </BitaButton>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Backtest section */}
      <div className={style.compositionPortfolio}>
        <div className={style.compositionPortfolioTitle}>Update Backtest</div>

        {/* Backtest Uploader */}
        <div className={styles.contentDataUpload} style={{ marginLeft: '18px' }}>
          <div className={style.dataUpload}>
            <div className={style.container}>
              <div>
                <div className={style.subtitle}>TEMPLATES</div>
                <div
                  style={{
                    display: 'flex',
                    gap: '20px',
                    marginTop: '10px',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                  }}
                >
                  <BitaButton
                    primaryWhite
                    width="160px"
                    height="35px"
                    onClick={() => {
                      FileSaver.saveAs(
                        `${process.env.PUBLIC_URL}/files/backtest_template.csv`,
                        `backtest_template.csv`,
                      );
                    }}
                  >
                    Backtest Template
                  </BitaButton>
                </div>
              </div>
              <div className={style.subtitle}>Backtest Uploader</div>
              <div
                className={style.containerButtonsUpload}
                // style={{
                //   display: 'flex',
                //   alignItems: 'flex-end',
                //   flexWrap: 'wrap',
                //   // pointerEvents: selectBasket?.value && data ? 'auto' : 'none',
                //   // opacity: selectBasket?.value && data ? 1 : 0.5,
                // }}
              >
                {/* <div>
                  <CSVReader
                    onUploadAccepted={results => {
                      setDataBacktest(results?.data);
                    }}
                  >
                    {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }) => (
                      <>
                        <div>
                          <button className={style.buttonUpload} type="button" {...getRootProps()}>
                            Select File
                          </button>
                          <div style={styles.acceptedFile} />
                        </div>
                        <div className={style.nameFile}>
                          {acceptedFile && `(${acceptedFile.name})`}
                          {acceptedFile && setFilesBacktest(acceptedFile)}
                        </div>
                      </>
                    )}
                  </CSVReader>
                </div> */}

                <div>
                  <input
                    className={style.buttonUpload}
                    type="file"
                    accept=".csv"
                    onChange={event => {
                      const file = event.target.files[0];
                      setFilesBacktest(file);
                    }}
                    style={{ display: 'none' }} // Oculta el elemento <input>
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className={style.buttonUpload}>
                    Select File
                  </label>
                  <div style={styles.acceptedFile} />
                </div>
                <BitaButton
                  disabled={!filesBacktest?.name || buttonStateLoading.basket}
                  width="160px"
                  onClick={() => {
                    setButtonStateLoading({ ...buttonStateLoading, backtest: true });
                    Papa.parse(filesBacktest, {
                      complete: results => {
                        if (
                          results.data.length > 1 &&
                          results.data[0][0].toUpperCase() === 'DATE' &&
                          results.data[0][1].toUpperCase() === 'VALUE'
                        ) {
                          setDataBacktest(results.data);
                        } else {
                          toast.error(
                            'Invalid file type structure. Please upload a file that matches the required format',
                          );
                        }
                        setButtonStateLoading({ ...buttonStateLoading, backtest: false });
                      },
                      header: false,
                    });
                  }}
                  primary
                  height="35px"
                >
                  {buttonStateLoading.backtest ? <BitaSpinner pointers /> : 'Upload File'}
                </BitaButton>
              </div>

              <p className={style.nameFile}>{filesBacktest && `(${filesBacktest?.name})`}</p>
            </div>
          </div>
          <div>{dataBacktest.length > 0 && <BacktestTable />}</div>
        </div>
      </div>
      {/* Basket Parameters section */}
      {/* {data && Object.keys(data ?? {}).length > 0 && ( */}
      {selectBasket && selectBasket.id && (
        <div className={style.compositionPorfolio}>
          <div className={style.compositionPorfolioTitle}>Basket Parameters</div>

          <div className={styles.contentWidget}>
            <div>Start Parameters</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <BitaInput
                    value={data?.name || ''}
                    type="text"
                    inputStyle={styles.nameInput}
                    title="Name"
                    externalTitle="Name"
                    placeholder="Name"
                    justify="flex-start"
                    handleChange={value => {
                      handleChange('name', value);
                    }}
                  />
                  <BitaInput
                    value={data?.description || ''}
                    type="text"
                    inputStyle={styles.nameInput}
                    title="Description"
                    externalTitle="Description"
                    placeholder="Description"
                    justify="flex-start"
                    handleChange={value => {
                      handleChange('description', value);
                    }}
                  />
                </div>
                <div
                  className={styles.contentInput}
                  style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}
                >
                  <div style={{ pointerEvents: 'none', opacity: '0.5' }}>
                    <BitaInput
                      disabled
                      value={selectBasket?.symbol ?? ''}
                      type="text"
                      inputStyle={styles.basketInput}
                      title="Ticker"
                      externalTitle="Ticker"
                    />
                  </div>
                  {data?.ric && hasValidRICAccess(user?.userdata.id_company) && (
                    <div style={{ pointerEvents: 'none', opacity: '0.5' }}>
                      <BitaInput
                        value={data?.ric || ''}
                        type="text"
                        name="ric"
                        inputStyle={styles.basketInput}
                        title="RIC"
                        maxlength={20}
                        externalTitle="RIC"
                      />
                    </div>
                  )}
                </div>

                {previousData?.divisor || data?.divisor ? (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '18px',
                      }}
                    >
                      <div>Automatically Adjust Divisor</div>
                      <div>
                        <Switcher.Switch
                          className={styles.customRadio}
                          checked={automaticallyAdjustDivisor}
                          onChange={() => {
                            setAutomaticallyAdjustDivisor(!automaticallyAdjustDivisor);
                          }}
                        />
                      </div>
                    </div>

                    {!automaticallyAdjustDivisor ? (
                      <div
                        className={styles.contentInput}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          justifyContent: 'flex-start',
                          alignItems: 'baseline',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '18px',
                          }}
                        >
                          <div>Divisor</div>
                        </div>

                        <BitaInput
                          value={data?.divisor}
                          type="number"
                          inputStyle={!data?.divisor ? styles.needValidation : styles.basketInput}
                          title="Divisor"
                          placeholder="Enter Value"
                          lower_limit={0}
                          handleChange={val => {
                            handleChange('divisor', val.replace(/,/g, ''));
                          }}
                        />
                      </div>
                    ) : (
                      <div className={style.contentDivisorDeterminationSetup}>
                        <BitaSelect
                          warningIcon
                          selected={data?.divisor_determination_setup}
                          value={
                            validateFields(
                              'divisor_determination_setup',
                              data?.divisor_determination_setup,
                            )
                              ? DATA_DIVISOR_DETERMINATION_SETUP.find(
                                  item => item.id === data?.divisor_determination_setup,
                                )
                              : ''
                          }
                          label="Divisor Determination Point"
                          externalPlaceholder="Divisor Determination Point"
                          data={DATA_DIVISOR_DETERMINATION_SETUP}
                          handleSelection={value => {
                            if (value?.value === 'IMMEDIATELY') {
                              setData({
                                ...data,
                                launch_point: '',
                                divisor_determination_setup: value?.id,
                              });
                            } else {
                              handleChange('divisor_determination_setup', value?.id);
                            }
                          }}
                          selectClassname={
                            !validateFields(
                              'divisor_determination_setup',
                              data?.divisor_determination_setup,
                            ) && style.needValidationSelect
                          }
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <></>
                )}

                <div
                  className={styles.contentInput}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-start',
                    alignItems: 'baseline',
                    flexDirection: 'column',
                  }}
                >
                  <BitaSelect
                    warningIcon
                    selected={data?.calculation_setup ?? ''}
                    value={
                      validateFields('calculation_setup', data?.calculation_setup)
                        ? {
                            value: data?.calculation_setup.replace('_', ' '),
                            id: data?.calculation_setup.replace('_', ' '),
                            name: data?.calculation_setup.replace('_', ' '),
                          }
                        : ''
                    }
                    externalPlaceholder="Calculation Frequency"
                    label="Calculation frequency"
                    data={[
                      { value: 'EOD Only', id: 'EOD' },
                      { value: 'EOD and Intra-day', id: 'EOD_INTRADAY' },
                    ]}
                    handleSelection={value => {
                      handleChange('calculation_setup', value?.id);
                      updateCoverageHelper(
                        data.meta_reference_data,
                        value?.id,
                        token,
                        setData,
                        setCoverageRequestLoading,
                      );
                    }}
                    selectClassname={
                      !validateFields('calculation_setup', data?.calculation_setup) &&
                      styles.needValidationSelect
                    }
                  />
                </div>

                <div
                  className={styles.contentInput}
                  style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}
                >
                  <BitaSelect
                    warningIcon
                    selected={data?.calculation_zone}
                    value={{
                      id: data?.calculation_zone,
                      value: calculationZoneMap.get(data?.calculation_zone)?.public_name,
                    }}
                    label="Calculation Zone"
                    externalPlaceholder="Calculation Zone"
                    data={filteredCalculationZones}
                    handleSelection={value => {
                      handleChange('calculation_zone', value?.id);
                    }}
                    selectClassname={!data?.calculation_zone && styles.needValidationSelect}
                  />
                </div>
              </div>
              <div className={style.contentNoMetadata} style={{ width: '28%' }}>
                <div className={style.tableNoMetadata}>
                  <div style={{ borderBottom: '1px solid #c7c7c7' }}>Calculation Zone Details</div>
                  <div style={{ display: 'flex' }}>
                    <div>Timezone: </div>{' '}
                    <div>
                      {getZoneValue(
                        calculationZoneMap,
                        data,
                        CALCULATION_ZONE_CONFIG.CALC_TIMEZONE,
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div>Calculation Start: </div>{' '}
                    <div>
                      {getZoneValue(
                        calculationZoneMap,
                        data,
                        CALCULATION_ZONE_CONFIG.CALC_OPEN_TIME,
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div>Calculation End: </div>{' '}
                    <div>
                      {getZoneValue(
                        calculationZoneMap,
                        data,
                        CALCULATION_ZONE_CONFIG.CALC_CLOSE_TIME,
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div>Start Week Day: </div>{' '}
                    <div>
                      {getZoneValue(
                        calculationZoneMap,
                        data,
                        CALCULATION_ZONE_CONFIG.CALC_START_WEEK_DAY,
                        true,
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div>End Week Day: </div>{' '}
                    <div>
                      {getZoneValue(
                        calculationZoneMap,
                        data,
                        CALCULATION_ZONE_CONFIG.CALC_END_WEEK_DAY,
                        true,
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div>Calculation EOD Time: </div>{' '}
                    <div>
                      {getZoneValue(
                        calculationZoneMap,
                        data,
                        CALCULATION_ZONE_CONFIG.CALC_EOD_TIME,
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.contentWidget}>
            <div>Return Type</div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '25px',
                  justifyContent: 'flex-start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '25px',
                    justifyContent: 'flex-start',
                    alignItems: 'end',
                  }}
                >
                  <BitaSelect
                    warningIcon
                    selected={data?.calculation_type} // This is return type
                    value={
                      validateFields('calculation_type', data?.calculation_type)
                        ? {
                            value: data?.calculation_type.replace('_', ' '),
                            id: data?.calculation_type,
                          }
                        : ''
                    }
                    label="Return calculation"
                    data={[
                      { value: 'NET RETURN', id: 'NET_RETURN' },
                      { value: 'TOTAL RETURN', id: 'TOTAL_RETURN' },
                      { value: 'PRICE RETURN', id: 'PRICE_RETURN' },
                    ]}
                    handleSelection={value => handleChange('calculation_type', value?.id)}
                    selectClassname={
                      !validateFields('calculation_type', data?.calculation_type) &&
                      styles.needValidationSelect
                    }
                  />
                  {data?.calculation_type === 'NET_RETURN' && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'end' }}>
                      <BitaSelect
                        width="330px"
                        warningIcon
                        selected={data?.withholding_tax_id ?? 'BITA Withholding Taxes'}
                        data={templateData?.map(template => {
                          return { value: template?.withholding_tax_name, id: template?.id };
                        })}
                        value={
                          validateFields('withholding_tax_id', data?.withholding_tax_id)
                            ? {
                                value: templateData?.find(
                                  item => item.id === data?.withholding_tax_id,
                                )?.withholding_tax_name,
                                id: data?.withholding_tax_id,
                              }
                            : {
                                value: 'BITA Withholding Taxes',
                                id: 'BITA Withholding Taxes',
                              }
                        }
                        externalPlaceholder="Withholding Taxes Template"
                        label="Withholding Taxes Template"
                        handleSelection={value => handleChange('withholding_tax_id', value?.id)}
                      />
                      <BitaButton
                        width="auto"
                        height="35px"
                        className={styles.withholdingTaxButton}
                        onClick={() => {
                          setOpenWithholdingModal(!openWithholdingModal);
                        }}
                      >
                        Manage Withholding Taxes
                      </BitaButton>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '18px',
                    }}
                  >
                    <div>Annualized Daily Decrement</div>
                    <div>
                      <Switcher.Switch
                        className={styles.customRadio}
                        onChange={() => {
                          setIsActiveAnnualizedYearlyDecrement(!isActiveAnnualizedYearlyDecrement);
                          handleChange('percentage_value', null);
                        }}
                        checked={isActiveAnnualizedYearlyDecrement}
                      />
                    </div>
                  </div>

                  <div>
                    {isActiveAnnualizedYearlyDecrement && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <BitaInput
                          value={data?.percentage_value ?? 0}
                          type="number"
                          upper_limit={100}
                          lower_limit={0}
                          inputStyle={styles.basketInput}
                          title="Percentage"
                          externalTitle="Percentage"
                          placeholder="Enter Percentage"
                          tooltip="Please indicate a percentage between 0 and 100"
                          handleChange={val => {
                            handleChange('percentage_value', val);
                          }}
                        />
                        <div style={{ marginTop: '15px', marginLeft: '30px' }}>
                          Day-Count Conversion: 360 Days
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.contentWidget}>
            <div>Corporate Actions Methodology</div>
            <div>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-start',
                  pointerEvents: 'none',
                }}
              >
                <div>BITA CORPORATE ACTIONS METHODOLOGY</div>
                <Switcher.Switch
                  className={styles.customRadio}
                  onChange={() => handleChange('corporate_actions_methodology', 'BITA')}
                  checked
                  // checked={data?.corporate_actions_methodology === 'BITA'} TODO: This needs to be integrated at some point.
                />
              </div>
              {/* ALL remember to remove the hardcode from corporate_actions_methodology  */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-start',
                  opacity: 0.5,
                  pointerEvents: 'none',
                }}
              >
                <div>CUSTOM METHODOLOGY</div>
                <Switcher.Switch
                  className={styles.customRadio}
                  onChange={() => handleChange('corporate_actions_methodology', 'CUSTOM')}
                  checked={false}
                  // checked={data?.corporate_actions_methodology !== 'BITA'} TODO: This needs to be integrated at some point.
                />
              </div>
              {data?.corporate_actions_methodology &&
                data?.corporate_actions_methodology !== 'BITA' && (
                  <div style={{ marginTop: '10px' }}>
                    <BitaSelect
                      warningIcon
                      selected={data?.calculation_type}
                      value=""
                      label="Methodology"
                      data={[]}
                      handleSelection={value => handleChange('', value?.id)}
                      style={{ fontSize: 13 }}
                    />
                  </div>
                )}
            </div>
          </div>
          {data?.cash_constituent && existsCashConstituentsInMetadata() && (
            <div className={styles.contentWidget}>
              <div>Cash Constituents Configuration</div>
              <div
                className={styles.contentInputCash}
                style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}
              >
                <BitaInput
                  value={data?.cash_constituent?.interest_rate_on_cash}
                  type="number"
                  style={{ with: '150px' }}
                  inputStyle={
                    !interestCashValid ? styles.needValidationCash : styles.basketInputCash
                  }
                  title="Interest Rate on Cash"
                  externalTitle="Interest Rate on Cash"
                  placeholder="Enter Value"
                  lower_limit={0}
                  handleChange={val => {
                    handleChange('cash_constituent', {
                      ...data?.cash_constituent,
                      interest_rate_on_cash: val.replace(/,/g, ''),
                    });
                  }}
                />
              </div>
            </div>
          )}
          {data?.calculation_type !== 'PRICE_RETURN' && (
            <div className={styles.contentWidget}>
              <div>Cash Distribution</div>
              <div
                className={styles.contentInput}
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                }}
              >
                <BitaSelect
                  warningIcon
                  width="330px"
                  selected={data?.cash_distribution}
                  value={
                    validateFields('cash_distribution', data?.cash_distribution)
                      ? DATA_CASH_DISTRIBUTION.find(item => item.id === data?.cash_distribution)
                      : ''
                  }
                  label="Cash Distribution"
                  externalPlaceholder="Cash Distribution"
                  data={DATA_CASH_DISTRIBUTION}
                  handleSelection={value => handleChange('cash_distribution', value?.id)}
                  selectClassname={
                    !validateFields('cash_distribution', data?.cash_distribution) &&
                    styles.needValidationSelect
                  }
                />

                <div>
                  <BitaSelect
                    warningIcon
                    width="330px"
                    selected={data?.application_point}
                    label="Application Point"
                    externalPlaceholder="Application Point"
                    value={
                      validateFields('application_point', data?.application_point)
                        ? DATA_APPLICATION_POINT.find(item => item.id === data?.application_point)
                        : ''
                    }
                    data={DATA_APPLICATION_POINT}
                    handleSelection={value => handleChange('application_point', value?.id)}
                    selectClassname={
                      !validateFields('application_point', data?.application_point) &&
                      style.needValidationSelect
                    }
                  />
                  {!validateFields('application_point', data?.application_point) && (
                    <div className={styles.errorMessage}>Please select an application point</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={styles.contentWidget}>
            <div>Currency</div>
            <div
              className={styles.contentInput}
              style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}
            >
              <BitaSelect
                warningIcon
                selected={data?.currency}
                value={
                  validateFields('currency', data?.currency)
                    ? {
                        value: data?.currency,
                        id: data?.currency,
                      }
                    : ''
                }
                label="Currency"
                data={CURRENCIES_ARRAY.map(item => {
                  return { value: item, id: item };
                })}
                handleSelection={value => handleChange('currency', value?.id)}
                selectClassname={
                  !data?.currency || !validateFields('currency', data?.currency)
                    ? styles.needValidationSelect
                    : ''
                }
              />
            </div>
          </div>

          <div className={styles.contentWidget}>
            <div>Dissemination Options</div>
            <div
              className={styles.contentInput}
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-start',
                opacity: 0.5,
                pointerEvents: 'none',
              }}
            >
              <BitaSelect
                warningIcon
                selected={data?.dissemination}
                value={
                  validateFields('dissemination', data?.dissemination)
                    ? {
                        value: data?.dissemination,
                        id: data?.dissemination,
                      }
                    : {
                        value: 'BITA Only',
                        id: 'BITA Only',
                      }
                }
                label="Dissemination"
                data={[
                  { value: 'BITA Only', id: 'BITA Only' },
                  { value: 'BITA + Bloomberg', id: 'BITA + Bloomberg', disabled: true },
                ]}
                handleSelection={value => handleChange('dissemination', value?.id)}
                style={{ fontSize: 13 }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Apply Button */}
      <div
        className="contentButtonApply"
        style={{
          width: '100%',
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <BitaButton onClick={handleApply} disabled={applyButtonValidations()} primary>
          Apply
        </BitaButton>
        {data?.meta_reference_data && applyButtonValidations() && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginLeft: '10px',
            }}
          >
            <BiSolidError className="icon-error" />
            <div>Cannot apply until errors are solved</div>
          </div>
        )}
        {data?.meta_reference_data &&
          (applyButtonValidations() || data?.meta_reference_data.length === 0) && (
            <div className="tooltipErrorApply">
              {existRequestCoverage() && (
                <p style={{ display: 'flex' }}>
                  {' '}
                  <BiSolidError className="icon-error" /> The basket contains some non-covered
                  instruments. Please review and make a decision.
                </p>
              )}
              {!interestCashValid && (
                <p style={{ display: 'flex' }}>
                  {' '}
                  <BiSolidError className="icon-error" />
                  Interest Rate on Cash is missing.
                </p>
              )}
              {data?.meta_reference_data?.length === 0 && (
                <p style={{ display: 'flex' }}>
                  {' '}
                  <BiSolidError className="icon-error" />
                  The basket contains no instruments.
                </p>
              )}
              {validateData.map(item => (
                <div>
                  {!validarData(data, [item]) && (
                    <p style={{ display: 'flex' }}>
                      {' '}
                      <BiSolidError className="icon-error" />{' '}
                      {`${validateDataLabel[item]} is missing.`}
                    </p>
                  )}
                </div>
              ))}
              {!automaticallyAdjustDivisor && !isDivisorOrStartingValueValid() && (
                <p style={{ display: 'flex' }}>
                  {' '}
                  <BiSolidError className="icon-error" /> Either the divisor or starting value is
                  missing/invalid. Please provide one of the two.
                </p>
              )}
            </div>
          )}
      </div>
      {/* Success Modal */}
      <BitaModal
        isModalOpen={showModal}
        onClick={() => {
          setShowModal(false);
          resetBasketData();
        }}
      >
        <div style={{ width: ' 600px ', minHeight: '300px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 0',
            }}
          >
            <span
              onClick={() => {
                setShowModal(false);
                resetBasketData();
              }}
              style={styleClose}
            >
              &times;
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div className={style.modalTextTitle}>Update successfully completed!</div>
            <div style={{ display: 'flex', margin: '5%', fontSize: '20px' }}>
              <Logo style={{ width: '100px', height: '100px', marginRight: '20px' }} />
              <div>
                <p> Updated ID: {selectBasket?.id}</p>
                <p> Updated Name: {data?.name}</p>
                <p> Updated Status: Transmitted to BITA ACE </p>
              </div>
            </div>
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}
          >
            <BitaButton primaryWhite onClick={() => history.push('/')} height="35px" width="200px">
              Go to Home
            </BitaButton>
            <BitaButton
              primaryWhite
              onClick={() => {
                setShowModal(false);
                resetBasketData();
              }}
              height="35px"
              width="200px"
            >
              Update another basket
            </BitaButton>
          </div>
        </div>
      </BitaModal>
      {/* Confirm Modal */}
      <BitaModal isModalOpen={showModalConfirm} setModalState={() => setShowModalConfirm(false)}>
        <div
          style={{
            width: '850px',
            minHeight: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              padding: '10px 0',
            }}
          >
            <Logo style={{ width: '30px', height: '30px', marginRight: '20px' }} />
            <h2
              style={{
                margin: '0',
                padding: '0',
                fontSize: '20px',
                textTransform: 'uppercase',
                textAlign: 'center',
                marginLeft: '-20px',
                width: '100%',
              }}
            >
              UPDATE BASKET
            </h2>
            <span onClick={() => setShowModalConfirm(false)} style={styleClose}>
              &times;
            </span>
          </div>
          <div>
            <div style={{ margin: '20px 0', textAlign: 'center', padding: '0px 5px' }}>
              Are you sure you want to update these parameters?
            </div>

            <div
              data-simplebar
              style={{ height: '460px', padding: '0px 5px' }}
              className="previewUpdateBasket"
            >
              <div className={tableStyles.tableTitle}>Basket Data Preview</div>
              <div
                className={tableStyles.tableContainer}
                style={{ width: '100%', flexDirection: 'column' }}
              >
                <div className={style.contentNoMetadata} style={{ width: '100%' }}>
                  <div className={style.tableNoMetadata}>
                    <ShowPreview
                      data={data}
                      templateData={templateData}
                      orgPrefix=""
                      automaticallyAdjustDivisor={automaticallyAdjustDivisor}
                      calculationZoneMap={calculationZoneMap}
                    />
                  </div>
                </div>

                {/* Summary table */}
                <div style={{ width: '100%' }}>
                  {data && (
                    <div className="tableUpdateBasket">
                      <BitaTable
                        data={renderRows(data?.meta_reference_data)}
                        search={false}
                        pagination
                        showFields={getColumns(data?.meta_reference_data)}
                        selectAction={() => {}}
                        CustomRowComponent={CustomRow}
                      />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                  <div style={{ width: '100%' }}>
                    {dataToshowOnSummary?.length > 0 && (
                      <div className="tableUpdateBasket">
                        <BitaTable
                          id="basket"
                          spacedRows
                          pagination
                          search={false}
                          equalColumnsWidth
                          selectAction={() => {}}
                          CustomRowComponent={CustomRow}
                          data={renderRowsUpdateTable(dataToshowOnSummary)}
                          showFields={getColumnsUpdateTable(dataToshowOnSummary)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div>{dataBacktest.length > 0 && <BacktestTable />}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <BitaButton
                onClick={handleSubmitButton}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
                disabled={buttonStateLoading.submit}
                autofocus
              >
                {buttonStateLoading.submit ? <BitaSpinner pointers /> : 'Submit'}
              </BitaButton>
              <BitaButton
                onClick={() => setShowModalConfirm(false)}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
              >
                Cancel
              </BitaButton>
            </div>
          </div>
        </div>
      </BitaModal>
      {/* showModalRequestCoverage Modal */}
      <BitaModal
        isModalOpen={showModalRequestCoverage}
        setModalState={() => setShowModalRequestCoverage(false)}
      >
        <div style={{ width: '600px ', minHeight: '380px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              padding: '10px 0',
            }}
          >
            <Logo style={{ width: '30px', height: '30px', marginRight: '20px' }} />
            <h2
              style={{
                margin: '0',
                padding: '0',
                fontSize: '20px',
                textTransform: 'uppercase',
                textAlign: 'center',
                marginLeft: '-20px',
                width: '100%',
              }}
            >
              REQUEST COVERAGE
            </h2>
            <span onClick={() => setShowModalRequestCoverage(false)} style={styleClose}>
              &times;
            </span>
          </div>
          <div>
            <div style={{ margin: '20px', textAlign: 'center' }}>
              We will send to the organization&apos;s e-mail the request for the following
              constituents
            </div>

            <div data-simplebar style={{ height: '360px' }}>
              <div style={{ width: '100%' }}>
                <div>Constituents</div>
                <div className={style.contentNoMetadata} style={{ width: '100%' }}>
                  <div className={style.tableNoMetadata}>
                    <div className={styles.requestCoverageEntryModal}>
                      <>
                        <div>ISIN</div>
                        <div>MIC</div>
                        {isUBSUser &&
                          !isClearStreetAmericasUser &&
                          data?.identifier_type !== IDENTIFIER_TYPE.ISIN && <div>SEDOL</div>}
                      </>
                    </div>
                    {(data?.meta_reference_data || []).map(item => {
                      if (item?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE) {
                        return (
                          <div className={styles.requestCoverageEntryModal}>
                            <>
                              <div>{item?.isin ?? '-'}</div>
                              <div>{item?.exchange ?? '-'}</div>
                              {isUBSUser &&
                                !isClearStreetAmericasUser &&
                                data?.identifier_type !== IDENTIFIER_TYPE.ISIN && (
                                  <div>{item?.sedol ?? '-'}</div>
                                )}
                            </>
                          </div>
                        );
                      }
                      return '';
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <BitaButton
                onClick={handleRequestCoverageSubmit}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
                autofocus
              >
                Submit
              </BitaButton>
              <BitaButton
                onClick={() => setShowModalRequestCoverage(false)}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
              >
                Cancel
              </BitaButton>
            </div>
          </div>
        </div>
      </BitaModal>
      {/* delete constituents modal Modal */}
      <BitaModal isModalOpen={showModalDelete} setModalState={() => setShowModalDelete(false)}>
        <div style={{ width: '600px ', minHeight: '380px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              padding: '10px 0',
            }}
          >
            <Logo style={{ width: '30px', height: '30px', marginRight: '20px' }} />
            <h2
              style={{
                margin: '0',
                padding: '0',
                fontSize: '20px',
                textTransform: 'uppercase',
                textAlign: 'center',
                marginLeft: '-20px',
                width: '100%',
              }}
            >
              Delete Constituents
            </h2>
            <span onClick={() => setShowModalDelete(false)} style={styleClose}>
              &times;
            </span>
          </div>
          <div>
            <div style={{ margin: '20px', textAlign: 'center' }}>
              Do you want to remove these constituents?
            </div>

            <div data-simplebar style={{ height: '360px' }}>
              <div style={{ width: '100%' }}>
                <div>Constituents</div>
                <div className={styles.contentNoMetadata} style={{ width: '100%' }}>
                  <div className={styles.tableNoMetadata}>
                    <div className={styles.requestCoverageEntry}>
                      <>
                        <div>ISIN</div>
                        <div>MIC</div>
                        {isUBSUser && data?.identifier_type !== IDENTIFIER_TYPE.ISIN && (
                          <div>SEDOL</div>
                        )}
                        <div>COMPANY NAME</div>
                      </>
                    </div>
                    {(constituentsDelete || []).map(item => (
                      <div className={styles.requestCoverageEntry}>
                        <>
                          <div>{item?.isin ?? '-'}</div>
                          <div>{item?.exchange ?? '-'}</div>
                          {isUBSUser && data?.identifier_type !== IDENTIFIER_TYPE.ISIN && (
                            <div>{item?.sedol ?? '-'}</div>
                          )}
                          <div>{item?.company_name ?? '-'}</div>
                        </>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <BitaButton
                onClick={() => {
                  setShowModalDelete(false);
                  const arrayIdentifier = constituentsDelete.map(item => item.isin ?? item.sedol);
                  const updatedConstituents = data.meta_reference_data.filter(
                    item => !arrayIdentifier.includes(item?.isin ?? item?.sedol),
                  );
                  setData({
                    ...data,
                    meta_reference_data: updatedConstituents,
                  });
                  updateCoverageHelper(
                    updatedConstituents,
                    data?.calculation_setup,
                    token,
                    setData,
                    setCoverageRequestLoading,
                  );
                  setConstituentsDelete([]);
                  const hasErrors = checkForErrors(updatedConstituents);
                  setErrorReferenceData(hasErrors);
                }}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
                autofocus
              >
                Delete
              </BitaButton>
              <BitaButton
                onClick={() => setShowModalDelete(false)}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
              >
                Cancel
              </BitaButton>
            </div>
          </div>
        </div>
      </BitaModal>
      {/* sucessful coverage request modal */}
      <BitaModal
        isModalOpen={showModalRequestCoverageSuccessful}
        setModalState={() => setShowModalRequestCoverageSuccessful(false)}
      >
        <div style={{ width: '600px ', minHeight: '130px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              padding: '10px 0',
            }}
          >
            <Logo style={{ width: '30px', height: '30px', marginRight: '20px' }} />
            <h2
              style={{
                margin: '0',
                padding: '0',
                fontSize: '20px',
                textTransform: 'uppercase',
                textAlign: 'center',
                marginLeft: '-20px',
                width: '100%',
              }}
            >
              REQUEST COVERAGE
            </h2>
            <span onClick={() => setShowModalRequestCoverageSuccessful(false)} style={styleClose}>
              &times;
            </span>
          </div>
          <div>
            <div style={{ margin: '20px', textAlign: 'center' }}>
              Your request is being processed, we will contact you shortly.
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <BitaButton
                onClick={() => setShowModalRequestCoverageSuccessful(false)}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
              >
                Close
              </BitaButton>
            </div>
          </div>
        </div>
      </BitaModal>

      <BitaModal isModalOpen={isDataLoading}>
        <div style={{ width: '300px ', minHeight: '130px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <h2 style={{ margin: '0 0 18px 0', padding: '0', fontSize: '22px' }}>Loading Basket</h2>
            <BitaSpinner />
          </div>
        </div>
      </BitaModal>
      <VisualizationModal
        withholdingTaxTemplateData={[
          { withholding_tax_name: 'BITA Withholding Taxes', id: 'default', org_id: 'BITA' },
          ...templateData,
        ]}
        updateTemplate={() => {
          getTemplates()
            .then(rsp => {
              const dataTax = [
                ...rsp?.data?.data,
                {
                  withholding_tax_name: 'BITA Withholding Taxes',
                  id: 'BITA Withholding Taxes',
                  custom: 'false',
                },
              ];
              setTemplateData(dataTax);
            })
            .catch(error => {
              toastInterceptor(error);
            });
        }}
        select={value => {
          handleChange('withholding_tax_id', value);
          getTemplates()
            .then(rsp => {
              const dataTax = [
                ...rsp?.data?.data,
                {
                  withholding_tax_name: 'BITA Withholding Taxes',
                  id: 'BITA Withholding Taxes',
                  custom: 'false',
                },
              ];
              setTemplateData(dataTax);
            })
            .catch(error => {
              toastInterceptor(error);
            });
        }}
        setModalState={setOpenWithholdingModal}
        isModalOpen={openWithholdingModal}
      />
      <AddInstrumentModal
        isOpen={showModalAdd}
        cashOption={!data?.meta_reference_data?.find(item => item?.isin?.includes('CASH'))}
        onClose={() => setShowModalAdd(false)}
        listInstruments={data?.meta_reference_data}
        listMic={listMIC}
        addInstrument={newInstrument => {
          setData(prev => {
            if (newInstrument.exchange === 'CASH') {
              return {
                ...prev,
                cash_constituent: { interest_rate_on_cash: null },
                meta_reference_data: [newInstrument, ...prev.meta_reference_data],
              };
            }
            return {
              ...prev,
              meta_reference_data: [newInstrument, ...prev.meta_reference_data],
            };
          });
          setErrorReferenceData(true);
        }}
      />
    </div>
  );
};

export default UpdateBasket;
